import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Shown under the label on wider screens. Keep it to a few words. */
  description?: string;
  /**
   * Decorative glyph above the label.
   *
   * Rendered inside an `aria-hidden` wrapper, so it never joins the option's
   * accessible name — the name has to stay exactly the label, and a test asserts
   * that. Same pattern as `Badge`'s icon slot.
   */
  icon?: ReactNode;
}

export interface SegmentedProps<T extends string> {
  /** Required: the group needs a name for screen readers. */
  label: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/**
 * A segmented control that is a real radio group.
 *
 * This replaces a row of bare `<button>`s, which is what the first version of
 * this app shipped: buttons have no selected state in the accessibility tree, so
 * a screen-reader user could hear all four audience options and no indication of
 * which one was active.
 *
 * What that costs is roughly forty lines of keyboard handling, because
 * `role="radio"` comes with an expected interaction contract:
 *
 *  - **Roving tabindex.** The group is one tab stop, not four. Only the selected
 *    option is tabbable; the arrow keys move within it.
 *  - **Arrow keys select as they move**, which is the standard behaviour for a
 *    radio group and is why Home/End are wired up too.
 *  - **Wrapping**, so Right on the last option returns to the first.
 *
 * On the layout: the tiles are stretched to a common height (`items-stretch`
 * plus a `min-h` floor) rather than left to size themselves. Four labels of
 * different lengths wrap to different line counts, and in an auto-height grid
 * that leaves the row visibly ragged — which is a poor first impression from the
 * first control on the page.
 */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: SegmentedProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  // The common-height floor only applies once something needs the vertical room.
  // Without it a three-word group of icon-less options gets tiles twice as tall
  // as their content.
  const hasIcons = options.some((option) => option.icon);
  // Whether the labels need to reserve a second line. See the note on the label
  // span below — this is what keeps the descriptions on a shared baseline.
  const hasDescriptions = options.some((option) => option.description);

  const move = (from: number, delta: number) => {
    const count = options.length;
    if (count === 0) return;
    // `+ count` keeps the modulo positive when delta is -1 at index 0.
    const next = (from + delta + count) % count;
    const option = options[next];
    if (!option) return;
    onChange(option.value);
    refs.current[next]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        move(index, 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        move(index, -1);
        break;
      case 'Home':
        event.preventDefault();
        move(0, 0);
        break;
      case 'End':
        event.preventDefault();
        move(options.length - 1, 0);
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'grid items-stretch gap-1.5 rounded-card border border-line bg-sunken p-1.5',
        // Two columns on a phone, not one. Stacked, this control measured 558px —
        // over two thirds of a 812px screen for four radio buttons, and the reason
        // no actual text field was visible without scrolling. Two columns halve
        // that without hiding either the icons or the descriptions, both of which
        // are how someone tells these four options apart.
        'grid-cols-2 lg:grid-cols-4',
        className
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            // Roving tabindex: the whole group is a single tab stop.
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'hover-lift flex flex-col items-start gap-1 rounded-field border p-3 text-left',
              hasIcons && 'min-h-24',
              selected
                ? 'surface-glow border-brand-300 bg-card text-strong dark:border-brand-500/60'
                : 'border-transparent text-muted hover:border-line hover:bg-card/70 hover:text-strong'
            )}
          >
            {option.icon ? (
              <span
                aria-hidden="true"
                className={cn(
                  'grid size-8 shrink-0 place-items-center rounded-field [&>svg]:size-4',
                  selected
                    ? 'bg-brand-600 text-white dark:bg-brand-500 dark:text-brand-950'
                    : 'bg-card text-subtle'
                )}
              >
                {option.icon}
              </span>
            ) : null}
            {/* `min-h-10` is two lines of `leading-5`, reserved whether or not
                this particular label needs the second one.

                Equal tile heights alone do not make the row look aligned. One
                label wrapping to two lines pushes only that tile's description
                down, so the descriptions end up on four different baselines
                inside four identically-sized boxes — which reads as sloppy in
                exactly the way the ragged heights did. `leading-5` is stated
                rather than inherited so the floor is provably two lines even if
                the theme's `text-sm` line-height changes. */}
            <span
              className={cn('block text-sm leading-5 font-semibold', hasDescriptions && 'min-h-10')}
            >
              {option.label}
            </span>
            {option.description ? (
              <span
                className={cn(
                  'block text-xs leading-tight',
                  selected ? 'text-muted' : 'text-subtle'
                )}
              >
                {option.description}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
